import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAdminAuth } from '../context/AdminAuthContext';
import { Button } from '@/components/ui/button';
import { 
  LayoutDashboard, 
  Users, 
  FileText, 
  CreditCard, 
  ShieldAlert, 
  ClipboardList,
  Settings,
  LogOut,
  Menu,
  X,
  Bot,
  ChevronDown
} from 'lucide-react';

const ADMIN_API_URL = `${process.env.REACT_APP_BACKEND_URL}/api/secure-admin`;

export const AdminLayout = ({ children }) => {
  const { admin, logout, token } = useAdminAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(true);

  const handleLogout = async () => {
    await logout();
    navigate('/secure-admin/login');
  };

  const navItems = [
    { path: '/secure-admin/dashboard', icon: LayoutDashboard, label: 'Dashboard', permission: 'view_dashboard' },
    { path: '/secure-admin/users', icon: Users, label: 'Users', permission: 'view_users' },
    { path: '/secure-admin/listings', icon: FileText, label: 'Listings', permission: 'view_listings' },
    { path: '/secure-admin/payments', icon: CreditCard, label: 'Payments', permission: 'view_payments' },
    { path: '/secure-admin/disputes', icon: ShieldAlert, label: 'Disputes', permission: 'view_disputes' },
    { path: '/secure-admin/audit-log', icon: ClipboardList, label: 'Audit Log', permission: 'view_audit_log' },
    { path: '/secure-admin/ai-risk', icon: Bot, label: 'AI Risk Center', permission: 'view_ai_risk' },
    { path: '/secure-admin/config', icon: Settings, label: 'Config', permission: 'view_config' },
  ];

  // Filter items by role permissions
  const rolePermissions = {
    owner: ['view_dashboard', 'view_users', 'view_listings', 'view_payments', 'view_disputes', 'view_audit_log', 'view_ai_risk', 'view_config'],
    ops: ['view_dashboard', 'view_users', 'view_listings', 'view_payments', 'view_disputes', 'view_audit_log', 'view_ai_risk', 'view_config'],
    support: ['view_dashboard', 'view_users', 'view_listings', 'view_payments', 'view_disputes'],
    auditor: ['view_dashboard', 'view_users', 'view_listings', 'view_payments', 'view_disputes', 'view_audit_log', 'view_ai_risk', 'view_config'],
  };

  const allowedPermissions = rolePermissions[admin?.role] || [];
  const filteredNavItems = navItems.filter(item => allowedPermissions.includes(item.permission));

  return (
    <div className="min-h-screen bg-gray-900 flex">
      {/* Sidebar */}
      <aside className={`${sidebarOpen ? 'w-64' : 'w-16'} bg-gray-800 border-r border-gray-700 flex flex-col transition-all duration-300`}>
        {/* Logo */}
        <div className="p-4 border-b border-gray-700 flex items-center justify-between">
          {sidebarOpen && (
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-blue-600 rounded flex items-center justify-center">
                <ShieldAlert className="w-5 h-5 text-white" />
              </div>
              <span className="font-bold text-white">Admin</span>
            </div>
          )}
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="text-gray-400 hover:text-white p-1"
          >
            {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-2 space-y-1">
          {filteredNavItems.map((item) => {
            const isActive = location.pathname === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center gap-3 px-3 py-2 rounded-lg transition-colors ${
                  isActive 
                    ? 'bg-blue-600 text-white' 
                    : 'text-gray-400 hover:bg-gray-700 hover:text-white'
                }`}
                data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
              >
                <item.icon className="w-5 h-5 flex-shrink-0" />
                {sidebarOpen && <span>{item.label}</span>}
              </Link>
            );
          })}
        </nav>

        {/* User Info */}
        <div className="p-4 border-t border-gray-700">
          {sidebarOpen ? (
            <div className="space-y-2">
              <div className="text-sm">
                <p className="text-white font-medium truncate">{admin?.name}</p>
                <p className="text-gray-500 text-xs truncate">{admin?.email}</p>
                <p className="text-blue-400 text-xs capitalize">{admin?.role}</p>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleLogout}
                className="w-full justify-start text-gray-400 hover:text-white hover:bg-gray-700"
                data-testid="admin-logout-btn"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </Button>
            </div>
          ) : (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleLogout}
              className="w-full p-2 text-gray-400 hover:text-white hover:bg-gray-700"
            >
              <LogOut className="w-5 h-5" />
            </Button>
          )}
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-auto">
        {children}
      </main>
    </div>
  );
};

export default AdminLayout;
